import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ClipboardDocumentListIcon,
  PlusIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import restaurantLogo from '../../assets/restaurant-logo.png'
import NotificationDropdown from './NotificationDropdown'
import ProfileDropdown from './ProfileDropdown'

const Header = ({ 
  restaurant, 
  user, 
  profile, 
  notifications = [],
  unreadNotifications = 0,
  setActiveTab,
  handleLogout,
  onMarkNotificationRead,
  onMarkAllNotificationsRead
}) => {
  const [showNotifications, setShowNotifications] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // Close dropdowns when clicking outside or when one opens
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-dropdown')) {
        setShowProfileMenu(false)
      }
      if (showNotifications && !event.target.closest('.notifications-dropdown')) {
        setShowNotifications(false)
      }
      if (showMobileMenu && !event.target.closest('.mobile-menu')) {
        setShowMobileMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showProfileMenu, showNotifications, showMobileMenu])

  // Close other dropdown when one opens
  const handleNotificationToggle = () => {
    setShowNotifications(!showNotifications)
    if (showProfileMenu) setShowProfileMenu(false)
    if (showMobileMenu) setShowMobileMenu(false)
  }

  const handleProfileToggle = () => {
    setShowProfileMenu(!showProfileMenu)
    if (showNotifications) setShowNotifications(false)
    if (showMobileMenu) setShowMobileMenu(false)
  }

  const handleMobileMenuToggle = () => {
    setShowMobileMenu(!showMobileMenu)
    if (showNotifications) setShowNotifications(false)
    if (showProfileMenu) setShowProfileMenu(false)
  }

  return (
    <>
      {/* Premium White Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 sm:h-22">
            
            {/* Left Section - Clean Brand */}
            <div className="flex items-center gap-6 min-w-0">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex-shrink-0 cursor-pointer"
                onClick={() => setActiveTab('overview')}
              >
                <img 
                  src={restaurantLogo} 
                  alt="Ordyrr Logo" 
                  className="h-10 w-auto object-contain"
                />
              </motion.div>
              
              <div className="min-w-0">
                <h1 className="text-xl font-black text-gray-900 tracking-tighter uppercase truncate leading-none mb-1.5">
                  {restaurant?.restaurant_name || restaurant?.name || 'Restaurant Manager'}
                </h1>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Live Now</span>
                  <div className="w-1 h-1 rounded-full bg-gray-200"></div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate max-w-[200px]">
                    {restaurant?.restaurant_address || restaurant?.address || 'Restaurant Address'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Section - Minimal Actions */}
            <div className="flex items-center gap-8">
              {/* Desktop Quick Actions */}
              <div className="hidden lg:flex items-center gap-4">
                <button
                  onClick={() => setActiveTab('orders')}
                  className="flex items-center gap-3 px-6 py-3 bg-gray-50 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-100 transition-all border border-gray-100"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 stroke-[3]" />
                  View Orders
                </button>
                
                <button
                  onClick={() => setActiveTab('menu')}
                  className="flex items-center gap-3 px-6 py-3 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-xl shadow-gray-200"
                >
                  <PlusIcon className="h-4 w-4 stroke-[3]" />
                  Add Dish
                </button>
              </div>

              {/* Notification & Profile */}
              <div className="flex items-center pl-4 border-l border-gray-100 space-x-3">
                <div className="notifications-dropdown">
                  <NotificationDropdown 
                    notifications={notifications}
                    unreadNotifications={unreadNotifications}
                    showNotifications={showNotifications}
                    setShowNotifications={setShowNotifications}
                    onMarkNotificationRead={onMarkNotificationRead}
                    onMarkAllNotificationsRead={onMarkAllNotificationsRead}
                  />
                </div>

                <div className="profile-dropdown">
                  <ProfileDropdown 
                    user={user}
                    profile={profile}
                    restaurant={restaurant}
                    showProfileMenu={showProfileMenu}
                    setShowProfileMenu={handleProfileToggle}
                    setActiveTab={setActiveTab}
                    handleLogout={handleLogout}
                  />
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden mobile-menu">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleMobileMenuToggle}
                    className="p-2 text-gray-500 hover:text-gray-900 rounded-lg transition-colors"
                  >
                    <AnimatePresence mode="wait">
                      {showMobileMenu ? (
                        <motion.div
                          key="close"
                          initial={{ rotate: -90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: 90, opacity: 0 }}
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="menu"
                          initial={{ rotate: 90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: -90, opacity: 0 }}
                        >
                          <Bars3Icon className="h-6 w-6" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40 lg:hidden"
              onClick={() => setShowMobileMenu(false)}
            />
            
            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="fixed top-20 left-4 right-4 bg-white rounded-xl shadow-lg border border-gray-200 z-50 lg:hidden overflow-hidden"
            >
              <div className="p-6 space-y-4">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => {
                    setActiveTab('orders')
                    setShowMobileMenu(false)
                  }}
                  className="w-full flex items-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors duration-200"
                >
                  <ClipboardDocumentListIcon className="h-5 w-5" />
                  <span className="font-semibold">Orders</span>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => {
                    setActiveTab('menu')
                    setShowMobileMenu(false)
                  }}
                  className="w-full flex items-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors duration-200"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span className="font-semibold">Add Dish</span>
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
